import * as React from "react";
import Outlet from "@/common/Outlet";
import { generatePath, Link } from "react-router";
import { FormattedMessage } from "react-intl";
import NavItem from "@/components/NavItem";
import { CompetitionSetupButton } from "@/components/CompetitionSetupButton";
import CompetitionJoinButton from "@/components/CompetitionJoinButton";
import { ScrollableScaffoldNav } from "@/components/ScrollableScaffoldNav";
import { useLocation } from "@/utils/location";
import { MetaLayout } from "@/common/MetaLayout";
import { useCompetitionMetaTags } from "@/utils/useMetaTags";
import { NotificationDot } from "@/kit/ui/notification-dot";
import { HeaderContainer } from "@/components/HeaderContainer";
import { Container } from "@/components/Container";
import { useLoginRedirect } from "@/utils/auth";
import { ActivityBanner } from "@/pages/_app.challenges/$slug/_banner";
import { Link as ButtonLink } from "@/kit/ui/link";
import { graphql, Environment } from "react-relay";
import { LoaderArgs } from "@/utils/route";
import { CompetitionScaffoldQuery as CompetitionScaffoldQueryType } from "./__generated__/CompetitionScaffoldQuery.graphql";
import { preload, usePreloaded } from "@/utils/relay";
import { ActivityNotFound } from "@/components/ActivityNotFound";
import { getOgImageUrl } from "@/utils/routing";
import { SubmissionDeadline } from "@/components/SubmissionDeadline";
import { useIsPast } from "@/utils/hooks";

export default function CompetitionScaffold() {
  const location = useLocation();
  const {
    query: { competitionBySlug: competition, viewer },
    variables: { eventSlug },
  } = useCompetitionScaffoldQuery();
  const to = useLoginRedirect();
  const { metaTitle, metaDescription } = useCompetitionMetaTags(
    competition?.title,
  );
  const [teamSettingsDot, setTeamSettingsDot] = React.useState(
    () => !competition?.membership,
  );
  const submissionDeadline = competition?.currentStage.submissionDeadline;
  const submissionsClosed = useIsPast(submissionDeadline);
  if (!competition?.event) {
    return (
      <ActivityNotFound kind="event">
        {!viewer && (
          <Link to={to}>
            <FormattedMessage defaultMessage="Try logging in to view this page." />
          </Link>
        )}
      </ActivityNotFound>
    );
  }
  if (!competition) {
    return (
      <ActivityNotFound kind="competition">
        {!viewer && (
          <Link to={to}>
            <FormattedMessage defaultMessage="Try logging in to view this page." />
          </Link>
        )}
      </ActivityNotFound>
    );
  }
  const competitionPath = (...rest: string[]) =>
    rest.length
      ? generatePath("/challenges/:owner/tracks/:slug/*", {
          owner: eventSlug,
          slug: competition.slug,
          "*": rest.join("/"),
        })
      : generatePath("/challenges/:owner/tracks/:slug", {
          owner: eventSlug,
          slug: competition.slug,
        });
  const templates = competition.templates.nodes;
  // A published version of the viewer's cloned workspace can be submitted to
  // this (simple/no-code) track directly from the header CTA — but not while
  // already on the submission page, to avoid confusion.
  const submitVersion = competition.currentStage.noCode
    ? (competition.viewerWorkspace?.publishedVersions.nodes[0] ?? null)
    : null;
  const showSubmit =
    !!submitVersion &&
    !submissionsClosed &&
    !location.pathname.startsWith(competitionPath("submission"));
  const joinButton = (
    <CompetitionJoinButton competition={competition}>
      {templates.length === 0 ? (
        competition.currentStage.noCode ? (
          !submissionsClosed &&
          !location.pathname.startsWith(competitionPath("submission")) && (
            <ButtonLink to={competitionPath("submission")}>
              <FormattedMessage defaultMessage="Create Submission" />
            </ButtonLink>
          )
        ) : (
          !location.pathname.startsWith(competitionPath("code")) && (
            <CompetitionSetupButton competition={competition} />
          )
        )
      ) : competition.viewerWorkspace ? (
        <div className="flex flex-row gap-2">
          <ButtonLink
            variant="secondary"
            to={`/${competition.viewerWorkspace.slug}`}
          >
            <FormattedMessage defaultMessage="Open workspace" />
          </ButtonLink>
          {showSubmit && (
            <ButtonLink variant="primary" to={competitionPath("submission")}>
              <FormattedMessage defaultMessage="Submit" />
            </ButtonLink>
          )}
        </div>
      ) : templates.length === 1 ? (
        <ButtonLink
          variant="primary"
          to={{
            pathname: `/${templates[0].workspace.slug}/versions/${templates[0].version}`,
            search: `?track=${competition.id}`,
          }}
        >
          <FormattedMessage defaultMessage="Open template" />
        </ButtonLink>
      ) : (
        !location.pathname.startsWith(competitionPath("templates")) && (
          <ButtonLink variant="primary" to={competitionPath("templates")}>
            <FormattedMessage defaultMessage="Browse templates" />
          </ButtonLink>
        )
      )}
    </CompetitionJoinButton>
  );
  return (
    <MetaLayout
      metaTitle={metaTitle}
      metaDescription={metaDescription}
      metaImageAlt={competition.title}

      metaImageUrl={getOgImageUrl(
        {
          type: "competition",
          owner: eventSlug,
          slug: competition.slug,
        },
        location.origin,
      )}
      hideTitleTemplate
    >
      <HeaderContainer>
        <ActivityBanner title={competition.title} banner={competition.banner} />
        <div className="flex sm:flex-row items-center sm:space-x-4 justify-between">
          <div className="flex flex-row justify-start items-center gap-2">
            <h1 className="font-semibold text-2xl leading-8 text-grey-900 line-clamp-2 break-all">
              <FormattedMessage
                defaultMessage="<prev>{challenge} / </prev>{slug}"
                values={{
                  challenge: eventSlug,
                  slug: competition.title,
                  prev: (chunks) => (
                    <span className="text-grey-500 whitespace-nowrap">
                      <Link
                        to={generatePath("/challenges/:slug", {
                          slug: eventSlug,
                        })}
                        className="hover:underline"
                      >
                        {chunks}
                      </Link>
                    </span>
                  ),
                }}
              />
            </h1>
          </div>
          <div className="flex flex-row items-center justify-start gap-2">
            {competition.viewerCanEdit && (
              <Link to={competitionPath("edit")}>
                <FormattedMessage defaultMessage="Edit" />
              </Link>
            )}
            <div>{joinButton}</div>
          </div>
        </div>
        {submissionDeadline && (
          <SubmissionDeadline deadline={submissionDeadline} />
        )}
        <ScrollableScaffoldNav className="-mb-px">
          <div className="flex justify-start items-center w-full">
            <NavItem to={competitionPath()} end>
              <FormattedMessage defaultMessage="Overview" />
            </NavItem>
            {!competition.currentStage.noCode && (
              <NavItem to={competitionPath("data")}>
                <FormattedMessage defaultMessage="Docs" />
              </NavItem>
            )}
            {!competition.currentStage.noCode && (
              <NavItem to={competitionPath("code/template")}>
                <FormattedMessage defaultMessage="Code" />
              </NavItem>
            )}
            {templates.length > 1 && (
              <NavItem to={competitionPath("templates")}>
                <FormattedMessage defaultMessage="Templates" />
              </NavItem>
            )}
            <NavItem to={competitionPath("rules")}>
              <FormattedMessage defaultMessage="Rules" />
            </NavItem>
            <NavItem to={competitionPath("discussions")}>
              <FormattedMessage defaultMessage="Discussion" />
            </NavItem>
            {competition.viewerCanEditMembers && (
              <NavItem to={competitionPath("members")}>
                <FormattedMessage defaultMessage="Members" />
              </NavItem>
            )}
            {competition.hasLeaderboard && (
              <NavItem to={competitionPath("leaderboard")}>
                <FormattedMessage defaultMessage="Leaderboard" />
              </NavItem>
            )}
            {competition.requiresApproval && (
              <NavItem to={competitionPath("approval")}>
                <FormattedMessage defaultMessage="Approval" />
              </NavItem>
            )}
            {viewer && competition.currentStage.noCode && (
              <NavItem to={competitionPath("submission")}>
                <FormattedMessage defaultMessage="Submission" />
              </NavItem>
            )}
            {competition.membership && (
              <NavItem
                onClick={() => {
                  setTeamSettingsDot(false);
                }}
                to={competitionPath("team")}
              >
                <FormattedMessage defaultMessage="Team Settings" />
                {teamSettingsDot && <NotificationDot />}
              </NavItem>
            )}
          </div>
        </ScrollableScaffoldNav>
      </HeaderContainer>
      <Container>
        <Outlet />
      </Container>
    </MetaLayout>
  );
}
const CompetitionScaffoldQuery = graphql`
  query CompetitionScaffoldQuery(
    $eventSlug: String!
    $competitionSlug: String!
  ) {
    viewer @ifAllowed {
      id
    }
    competitionBySlug(slug: $competitionSlug) {
      id
      slug
      title
      shortDescription
      description
      createdAt
      banner
      thumbnail
      viewerCanEdit: viewerCan(action: UPDATE_COMPETITION)
      viewerCanEditMembers: viewerCan(action: ADD_COMPETITION_MEMBER)
      viewerCanApprove: viewerCan(action: CREATE_PROJECT_VERSION_APPROVAL)
      isPrivate
      hasLeaderboard
      requiresApproval
      currentStage {
        noCode
        submissionDeadline
      }
      membership {
        id
      }
      templates(first: 2) {
        nodes {
          id
          version
          workspace {
            slug
          }
        }
      }
      viewerWorkspace @ifAllowed {
        id
        slug
        publishedVersions: versions(first: 1, filters: { published: true }) {
          nodes {
            id
            version
          }
        }
      }
      host {
        id
        displayName
        username
        ...EntityProfilePicFragment @arguments(thumbnail: true)
      }
      event(slug: $eventSlug) {
        id
      }
      ...CompetitionSetupButtonFragment
      ...CompetitionJoinButtonFragment
    }
  }
`;
export const loader = (environment: Environment) => {
  return ({ params: { slug: owner, track: slug } }: LoaderArgs) => {
    if (!slug || !owner) {
      throw new Response("No slug", { status: 500 });
    }
    return preload<CompetitionScaffoldQueryType>(
      environment,
      CompetitionScaffoldQuery,
      {
        competitionSlug: slug,
        eventSlug: owner,
      },
    );
  };
};
const useCompetitionScaffoldQuery = () =>
  usePreloaded<CompetitionScaffoldQueryType>();
